/**
 * @typedef {import('./index.js').Store<State, Getters>} Store
 */
/**
 * @template T
 * @typedef {import('./index.js').GetterFunction<T, State, Getters>} GetterFunction<T>
 */
/**
 * @template T
 * @template R
 * @typedef {import('./index.js').ActionFunction<T, R, State, Getters>} ActionFunction<T,R>
 */
/**
 * @typedef {Object<string,string>} TranslatedScopes
 * @description Key pair of translations in a scope
 */
/**
 * @typedef {Object<string,TranslatedScopes|string>} Translations
 */
import get from 'lodash.get'
import Mustache from 'mustache'
/**
 * @module Store:i18n
 * @description Internacionalization module
 */
export default {
  namespaced: true,
  /** i18n State
   * @typedef {Object} State
   * @property {Translations} translations - Translated labels registered by scope
   */
  /** @type {State} */
  state: {
    translations: {},
  },
  /** Global Getters
   * @typedef {Object} Getters
   * @property {GetterFunction<function(string, Object): string>} tc - Returns the translated label if it exists based on the scoped path set and substitute values present in the label using [Mustache]{@link https://github.com/janl/mustache.js/} with the variables passed, ex: tc('scope.label', { variable: 'value' })
   */
  /** @type {Getters} */
  getters: {
    tc: (state) => (path, variables = {}) => {
      const translation = get(state.translations, path, '') || ''
      return Mustache.render(translation, variables)
    }
  },
  /** Mutations */
  mutations: {
    REGISTER_LANG(state, lang) {
      state.translations = {
        ...state.translations,
        ...lang
      }
    },
  },
  /** Global Actions
   * @typedef {Object} Actions
   * @property {ActionFunction<{scope: string, lang: TranslatedScopes, overide: boolean},void>} registerScopedLang - Register a scoped set of {@link TranslatedScopes} if the set scope is not present. Pass { overide: true } to force registration even when alredy present.
   * @property {ActionFunction<{scope: string, lang: Translations, overide: boolean},void>} registerLang - Register a set of {@link Translations}, if there are labels unscoped in the {@link Translations} pass a scope and the unscoped items will be set in this scope. Pass { overide: true } to force registration even when alredy present.
   */
  /** @type {Actions} */
  actions: {
    registerScopedLang({ commit, state }, { scope, lang, overide }) {
      const currentScope = state.translations[scope]
      const scopedLang = lang[scope] || lang
      if (!currentScope || overide) {
        commit('REGISTER_LANG', { [scope]: scopedLang })
      }
    },
    registerLang({ commit, state }, { scope, lang, overide }) {
      const currentScope = state.translations[scope]
      const scopedLang = {}
      if (scope) {
        scopedLang[scope] = {}
        Object.keys(lang).forEach((key) => {
          if (typeof lang[key] === 'string') {
            scopedLang[scope][key] = lang[key]
            delete lang[key]
          }
        })
      }
      if (!currentScope || overide) {
        commit('REGISTER_LANG', { ...scopedLang, ...lang })
      }
    },
  }
}
